import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { InputField, Button, CircularLoader, NoticeBox } from '@dhis2/ui';

const SetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const token = searchParams.get('token');
  const userId = searchParams.get('userId');
  const username = searchParams.get('username');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('setting');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  // Password strength checker
  const checkPasswordStrength = (pwd) => {
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (pwd.match(/[a-z]/) && pwd.match(/[A-Z]/)) strength++;
    if (pwd.match(/\d/)) strength++;
    if (pwd.match(/[^a-zA-Z\d]/)) strength++;
    return strength;
  };

  useEffect(() => {
    setPasswordStrength(checkPasswordStrength(password));
  }, [password]);

  useEffect(() => {
    if (!token) {
      setError('No invitation token found. Please check your invitation link.');
      setStep('error');
      return;
    }
    setStep('setting');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // ✅ FIX: Use the same URL pattern as StaffForm
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' 
        ? '/backend-api' 
        : 'http://localhost:3001');
      
      const url = `${API_BASE_URL}/api/set-password`;
      
      console.log('📧 Setting password via Node.js backend...');
      console.log(`📧 API URL: ${url}`);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          userId,
          username,
          password,
        }),
      });

      console.log('📧 Response status:', response.status);
      
      const text = await response.text();
      console.log('📧 Response text (first 200 chars):', text.substring(0, 200));
      
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(`Expected JSON but got HTML. This means the request went to DHIS2 instead of Node.js backend.`);
      }

      if (response.ok) {
        setStep('success');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.error || 'Failed to set password. Please try again.');
      }
    } catch (err) {
      console.error('Password setup failed:', err);
      setError(err.message || 'Failed to set password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = () => {
    if (passwordStrength <= 1) return '#ff4d4f';
    if (passwordStrength === 2) return '#faad14';
    if (passwordStrength === 3) return '#52c41a';
    return '#1890ff';
  };

  const getStrengthLabel = () => {
    if (passwordStrength === 0) return 'Very Weak';
    if (passwordStrength === 1) return 'Weak';
    if (passwordStrength === 2) return 'Fair';
    if (passwordStrength === 3) return 'Good';
    return 'Strong';
  };

  const containerStyles = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '1rem',
  };

  const cardStyles = {
    maxWidth: '480px',
    width: '100%',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    padding: '2.5rem',
    animation: 'slideUp 0.5s ease-out',
  };

  const headerStyles = {
    textAlign: 'center',
    marginBottom: '2rem',
  };

  const titleStyles = {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#1a1a2e',
    marginBottom: '0.5rem',
    letterSpacing: '-0.5px',
  };

  const subtitleStyles = {
    color: '#6b7280',
    fontSize: '0.95rem',
    lineHeight: '1.5',
  };

  const formGroupStyles = {
    marginBottom: '1.25rem',
  };

  const strengthBarStyles = {
    marginTop: '0.5rem',
    height: '4px',
    background: '#e5e7eb',
    borderRadius: '4px',
    overflow: 'hidden',
    position: 'relative',
  };

  const strengthFillStyles = {
    height: '100%',
    width: `${(passwordStrength / 4) * 100}%`,
    background: getStrengthColor(),
    borderRadius: '4px',
    transition: 'all 0.3s ease',
  };

  const strengthLabelStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.75rem',
    color: '#6b7280',
    marginTop: '0.25rem',
  };

  const errorStyles = {
    marginBottom: '1rem',
  };

  const buttonContainerStyles = {
    marginTop: '1.5rem',
  };

  const footerStyles = {
    textAlign: 'center',
    marginTop: '1.5rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid #e5e7eb',
  };

  const footerTextStyles = {
    fontSize: '0.875rem',
    color: '#6b7280',
  };

  const inlineButtonStyles = {
    background: 'none',
    border: 'none',
    color: '#667eea',
    cursor: 'pointer',
    fontWeight: '600',
    padding: '0',
    textDecoration: 'underline',
  };

  // Inject animation keyframes
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  if (step === 'error') {
    return (
      <div style={containerStyles}>
        <div style={{ ...cardStyles, animation: 'fadeIn 0.3s ease-out' }}>
          <NoticeBox critical title="Invitation Error">
            {error || 'Invalid or expired invitation token. Please request a new invitation.'}
          </NoticeBox>
          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
            <Button onClick={() => navigate('/login')}>Go to Login</Button>
            <Button secondary onClick={() => window.location.href = '/'}>
              Go Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div style={containerStyles}>
        <div style={{ ...cardStyles, animation: 'fadeIn 0.3s ease-out' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎉</div>
            <NoticeBox success title="Success!">
              Your password has been set successfully.
              <br />
              <span style={{ fontSize: '0.875rem' }}>Redirecting to login...</span>
            </NoticeBox>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyles}>
      <div style={cardStyles}>
        <div style={headerStyles}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🔐</div>
          <h1 style={titleStyles}>Set Your Password</h1>
          <p style={subtitleStyles}>
            Welcome, <strong style={{ color: '#1a1a2e' }}>{username || 'User'}</strong>!
            <br />
            Please create a secure password to continue.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={formGroupStyles}>
            <InputField
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={({ value }) => setPassword(value)}
              required
              disabled={loading}
              placeholder="Enter your new password"
              helpText="Password must be at least 8 characters"
            />
            {password && (
              <>
                <div style={strengthBarStyles}>
                  <div style={strengthFillStyles} />
                </div>
                <div style={strengthLabelStyles}>
                  <span>Password strength:</span>
                  <span style={{ color: getStrengthColor(), fontWeight: '600' }}>
                    {getStrengthLabel()}
                  </span>
                </div>
              </>
            )}
          </div>

          <div style={formGroupStyles}>
            <InputField
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={({ value }) => setConfirmPassword(value)}
              required
              disabled={loading}
              placeholder="Confirm your password"
              helpText={confirmPassword && password !== confirmPassword ? 'Passwords do not match' : ''}
              validationText={confirmPassword && password !== confirmPassword ? 'Passwords do not match' : ''}
              error={confirmPassword && password !== confirmPassword}
            />
          </div>

          <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              id="showPassword"
              checked={showPassword}
              onChange={() => setShowPassword(!showPassword)}
              style={{ cursor: 'pointer' }}
            />
            <label htmlFor="showPassword" style={{ fontSize: '0.875rem', color: '#6b7280', cursor: 'pointer' }}>
              Show passwords
            </label>
          </div>

          {error && (
            <div style={errorStyles}>
              <NoticeBox critical>{error}</NoticeBox>
            </div>
          )}

          <div style={buttonContainerStyles}>
            <Button
              primary
              block
              type="submit"
              loading={loading}
              disabled={loading || !password || !confirmPassword}
              style={{ height: '48px', fontSize: '1rem', fontWeight: '600' }}
            >
              {loading ? 'Setting Password...' : 'Set Password'}
            </Button>
          </div>
        </form>

        <div style={footerStyles}>
          <p style={footerTextStyles}>
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              style={inlineButtonStyles}
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetPasswordPage;